import { z } from "zod";

/**
 * A client an admin obtained from the authorization server out of band.
 *
 * This is the first rung of the client ladder the MCP authorization
 * specification (2026-07-28) sets — **pre-registered → CIMD → DCR** — and it is
 * the only one that can carry a client the server treats as confidential. It
 * exists for the authorization server that accepts neither of the other two: one
 * advertising no `registration_endpoint` and no client-id metadata document
 * support refuses every automatic mechanism, and without this the whole server is
 * unconnectable.
 *
 * ## It belongs to the ISSUER, not to the server
 *
 * `OAuthClientRegistration` is keyed `@@unique([organizationId, issuer])`, and
 * one authorization server can guard several MCP servers. So the client supplied
 * here is established for the ISSUER that discovery resolves, and every later
 * flow against that issuer — for this server or any other — finds the stored row
 * and never reads this field again.
 *
 * That is also why it is not part of `RegisterMcpServerBodySchema`: the issuer is
 * not known until discovery runs, which happens when a flow STARTS. A client
 * typed in beside one server at registration time would be a second, competing
 * client for every other server that issuer guards.
 *
 * ## Supplying one for an issuer that already has a client is REFUSED
 *
 * It is not merged, not compared and not silently dropped:
 * `MCP_OAUTH_CLIENT_ALREADY_ESTABLISHED`. Every token already issued is bound to
 * the FIRST `client_id`, so replacing the row would orphan all of them — and the
 * symptom of that is a refresh failing weeks later with `invalid_client`, which
 * reads as the server revoking a grant. Discarding it quietly instead would be
 * worse still: an admin who deliberately typed a credential would get a
 * successful-looking connection that ignored it.
 */
export const PreRegisteredOAuthClientSchema = z.object({
  /**
   * The `client_id` the authorization server assigned.
   *
   * TRIMMED, because an OAuth `client_id` is a token and surrounding whitespace
   * is never part of one — it is a paste artefact, and refusing it at the far end
   * produces an error about an unknown client.
   */
  clientId: z
    .string()
    .trim()
    .min(1, "clientId must not be blank")
    .max(512, "clientId must be at most 512 characters"),
  /**
   * The secret, when the server issued one. Omit it for a public client.
   *
   * WRITE-ONLY, and returned by no endpoint on this surface: it is sealed into
   * `OAuthClientRegistration.clientSecret` by the write adapter, like every other
   * secret here.
   *
   * NOT trimmed, unlike `clientId`. Whitespace can be part of a secret, and
   * silently altering a credential before storing it produces an authentication
   * failure that points at the authorization server instead of at us.
   */
  clientSecret: z
    .string()
    .min(1, "clientSecret must not be blank")
    .max(2048, "clientSecret must be at most 2048 characters")
    .optional()
});

export type PreRegisteredOAuthClient = z.infer<typeof PreRegisteredOAuthClientSchema>;

/**
 * The body of `POST /api/mcp-servers/:id/oauth/start`.
 *
 * Every field is optional and the body as a whole may be absent, because the
 * ordinary connection supplies nothing: the ladder establishes a client by CIMD
 * or dynamic registration without help, and that is the path almost every server
 * takes. This body is the escape hatch for the authorization server that offers
 * neither.
 *
 * A nested object rather than flat `clientId` / `clientSecret` fields, so that a
 * secret without an id is unrepresentable rather than validated away.
 *
 * 🔴 **`.default({})` is what makes an ABSENT body legal, and it belongs here
 * rather than at the controller.**
 *
 * What `@Body()` yields depends on the `Content-Type`, not on whether a payload
 * was sent — the JSON body parser only engages for a JSON content type and
 * otherwise never assigns `req.body` at all. Measured against the Nest app the
 * MCP HTTP harness builds, by reporting the value the handler received:
 *
 * | the request | `@Body()` |
 * |---|---|
 * | no payload and NO `Content-Type` | `undefined` |
 * | `Content-Type: application/json`, empty payload | `{}` |
 * | a JSON object | the object |
 * | `Content-Type: text/plain` WITH a payload | `undefined` |
 *
 * So `undefined` is the ordinary shape for a caller that sends nothing, and a
 * bare `.parse(body)` without this default returns 400 to exactly that caller —
 * which is every connection needing no hand-registered client. The last row is
 * the one worth keeping in view: a non-JSON content type produces `undefined`
 * even though a body WAS sent, so this default is doing more than absorbing an
 * empty request.
 *
 * Coalescing in the controller instead (`parse(body ?? {})`) works and costs more
 * than it looks: it moves a statement about the CONTRACT into a handler, and it
 * hides the parse from `nexus/require-body-validation`, which matches the parse
 * against the `@Body()` parameter itself and reports an expression derived from
 * it as unvalidated.
 */
export const StartMcpServerOAuthBodySchema = z
  .object({
    preRegisteredClient: PreRegisteredOAuthClientSchema.optional()
  })
  .default({});

export type StartMcpServerOAuthBody = z.infer<typeof StartMcpServerOAuthBodySchema>;
